'use client';

import { Ninetailed } from '@ninetailed/experience.js';
import { NinetailedPreviewPlugin } from '@ninetailed/experience.js-plugin-preview';
import { type AbTestExposureContext, trackAbTestExposure } from 'helpers/analytics/abTestEvents';


const CLIENT_ID = process.env.NEXT_PUBLIC_NINETAILED_CLIENT_ID ?? '';
const NINETAILED_ENVIRONMENT = process.env.NEXT_PUBLIC_NINETAILED_ENVIRONMENT ?? 'main';
const IS_PRODUCTION = process.env.NEXT_PUBLIC_IS_CONTENTFUL_PRODUCTION === 'true';

let _isPreviewMode = false;

export const setPreviewMode = (isPreview: boolean): void => {
  if (_isPreviewMode === isPreview) return;
  _isPreviewMode = isPreview;

  // The preview plugin is only wired at Ninetailed construction time (see getNt()).
  // If preview mode becomes enabled after the singleton already exists, reset so
  // the next getNt() call can re-initialize with the preview plugin.
  if (IS_PRODUCTION && isPreview && _nt && !_previewPlugin) {
    _nt = null;
    _previewPlugin = null;
    _previewChangeListenerAttached = false;
  }
};

const shouldShowPreviewPlugin = (): boolean => (IS_PRODUCTION ? _isPreviewMode : true);

let _nt: Ninetailed | null = null;
let _previewPlugin: NinetailedPreviewPlugin | null = null;
let _previewChangeListenerAttached = false;

const _experiences = new Map<string, any>();
const getExperiences = () => Array.from(_experiences.values());
// The plugin also derives audiences from experiences, but its flyout reads the
// raw audienceDefinitions, so keep them populated from the experiences we have.
const getAudiences = () =>
  getExperiences()
    .map((experience) => experience?.audience)
    .filter(Boolean);

const registerExperience = (experience: any) => {
  if (!experience?.id) return;
  _experiences.set(experience.id, experience);

  // Update the mounted plugin in place — never recreate it, as a second instance
  // becomes inactive when the widget container already exists.
  const plugin = _previewPlugin as any;
  if (plugin) {
    plugin.experiences = getExperiences();
    plugin.audienceDefinitions = getAudiences();
    plugin.onChange?.();
  }
};

// The imperative onSelectVariant wires its middleware from the plugins active at
// subscription time, and the preview widget activates asynchronously (it lazy-loads).
// Wait for it so the first flyout selection applies without needing a navigation.
const waitForPreviewPluginReady = async (): Promise<void> => {
  const plugin = _previewPlugin as any;
  if (!plugin) return;
  for (let attempt = 0; attempt < 20 && !plugin.isActiveInstance; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

// The plugin's global reset() only resets the visitor profile — it does NOT clear
// forced variant/audience/variable selections. Wrap it so a reset also drops those
// overrides and then re-evaluates the slots via nt.page().
const patchPreviewReset = () => {
  const plugin = _previewPlugin as any;
  if (!plugin || plugin.__resetPatched) return;

  const originalReset = typeof plugin.reset === 'function' ? plugin.reset.bind(plugin) : null;
  plugin.reset = () => {
    plugin.experienceVariantIndexOverwrites = {};
    plugin.audienceOverwrites = {};
    plugin.variableOverwrites = {};
    plugin.onChange?.();
    const result = originalReset ? originalReset() : undefined;
    // Re-evaluate so slots return to their un-forced variant immediately.
    void _nt?.page().catch((err) => console.error('[Ninetailed] reset re-evaluation failed:', err));
    return result;
  };
  plugin.__resetPatched = true;
};

// The imperative nt.onSelectVariant() subscriptions only recompute when a profile
// event flows through the SDK — a preview selection alone does not trigger one.
// Listen to the plugin's onChangeEmitter and call nt.page() so every slot's
// onSelectVariant re-fires with the newly forced variant/audience.
const attachPreviewOverrideListener = () => {
  if (!_nt || !_previewPlugin || _previewChangeListenerAttached) return;

  const plugin = _previewPlugin as any;
  const emitter = plugin.onChangeEmitter;
  if (!emitter || typeof emitter.addListener !== 'function') return;

  let lastSignature = '';
  let reevaluating = false;
  let queued = false;

  const reevaluate = () => {
    if (!_nt) return;
    if (reevaluating) {
      queued = true;
      return;
    }
    reevaluating = true;
    void _nt
      .page()
      .catch((err) => console.error('[Ninetailed] preview re-evaluation failed:', err))
      .finally(() => {
        reevaluating = false;
        if (queued) {
          queued = false;
          reevaluate();
        }
      });
  };

  emitter.addListener(() => {
    const signature = JSON.stringify({
      exp: plugin.experienceVariantIndexOverwrites ?? {},
      aud: plugin.audienceOverwrites ?? {},
      var: plugin.variableOverwrites ?? {},
    });
    if (signature === lastSignature) return;
    lastSignature = signature;
    reevaluate();
  });

  _previewChangeListenerAttached = true;
};

export const getNt = (): Ninetailed => {
  if (!_nt) {
    const plugins: any[] = [];
    
    if (shouldShowPreviewPlugin()) {
      _previewPlugin = new NinetailedPreviewPlugin({
        experiences: getExperiences(),
        audiences: getAudiences(),
      });
      plugins.push(_previewPlugin);
    }

    _nt = new Ninetailed({ clientId: CLIENT_ID, environment: NINETAILED_ENVIRONMENT }, {
      useSDKEvaluation: true,
      plugins,
    } as any);
    patchPreviewReset();
    attachPreviewOverrideListener();
  }
  return _nt;
};

export const getNinetailedComponent = async (
  componentData: any,
  locale: string,
  onVariantResolved: (variant: any, exposure: AbTestExposureContext | null) => void,
): Promise<(() => void) | undefined> => {
  if (!CLIENT_ID) {
    console.warn('[Ninetailed] NEXT_PUBLIC_NINETAILED_CLIENT_ID is not configured; skipping personalization.');
    return;
  }

  const ntExpEntry = componentData?.componentData;
  const ntExperienceEntryId = ntExpEntry?.fields?.nt_experience_id as string | undefined;
  if (!ntExperienceEntryId) {
    console.warn('[Ninetailed] Missing nt_experience_id');
    return;
  }

  // Contentful is fetched server-side to keep the access token out of the browser bundle.
  // The API route lives under app/[locale]/api/... so the locale segment must be included.
  const res = await fetch(`/${locale}/api/ninetailed-experience/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ntExperienceEntryId }),
  });

  if (!res.ok) {
    console.error('[Ninetailed] API route error:', await res.text());
    return;
  }

  const { mappedExperience, baselineEntry } = await res.json();
  const baseline = Object.assign({ id: baselineEntry.sys.id }, baselineEntry);

  // Make this experience visible in the preview flyout, then init/reuse the SDK.
  if (shouldShowPreviewPlugin()) {
    registerExperience(mappedExperience);
  }
  const nt = getNt();

  // Wait for the preview widget to be ready so the first flyout selection applies.
  await waitForPreviewPluginReady();

  let unsubscribe: (() => void) | null = null;
  let lastSelectionKey: string | null = null;

  unsubscribe = nt.onSelectVariant({ baseline, experiences: [mappedExperience] }, (state) => {
    if (state.status === 'error') {
      console.error('[Ninetailed] error:', state.error);
      // Fall back to baseline on error
      onVariantResolved(baselineEntry, null);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const variant = state.variant as any;
    const resolvedEntry = variant ?? baselineEntry;
    const expName = state.experience?.name ?? 'none (showing baseline)';

    // Dedupe by a richer key than variantIndex alone.
    // Preview controls can produce valid state updates where index is unchanged
    // but experience/variant resolution has changed.
    const variantEntityId = variant?.id ?? variant?.sys?.id ?? baseline.id;
    const selectionKey = [
      state.experience?.id ?? 'none',
      String(state.variantIndex ?? 0),
      variantEntityId,
      state.loading ? 'loading' : state.status,
    ].join('|');
    if (lastSelectionKey === selectionKey) {
      return;
    }
    lastSelectionKey = selectionKey;

    // Resolve the component — this is what renders the variant on screen.
    // Clone to ensure React state updates even if SDK returns the same object
    // reference for a forced preview selection.
    const resolvedVariant = { ...(resolvedEntry as Record<string, unknown>) };

    let exposure: AbTestExposureContext | null = null;

    if (!state.loading && state.status === 'success') {
      const variantName =
        (resolvedEntry?.name as string | undefined) ??
        (resolvedEntry?.fields?.name as string | undefined) ??
        `variant-${String(state.variantIndex ?? 'unknown')}`;

      exposure = trackAbTestExposure({
        experimentId: state.experience?.id ?? 'unknown-experiment',
        experimentName: expName,
        variantIndex: state.variantIndex,
        shownVariantEntryTitle: variantName,
        userSessionIdentifier: (nt as { profile?: { id?: string } }).profile?.id,
      });

      // Track which variant was seen
      if (state.experience && state.variantIndex !== undefined) {
        nt.trackStickyComponentView({
          experienceId: state.experience.id,
          componentId: variantEntityId,
          variantIndex: state.variantIndex,
        }).catch((err) => console.error('[Ninetailed] trackStickyComponentView error:', err));
      }
    }

    onVariantResolved(resolvedVariant, exposure);
  });

  // page() is already called by RouteTracker in NinetailedProvider on every
  // navigation. Calling it again here is redundant and can cause double-counting
  // of page views. Remove the call so profile events are fired exactly once.

  // Return a cleanup function so callers can unsubscribe the listener on unmount.
  // Note: We keep the subscription active to allow variant changes from preview controls
  return () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };
};
